/**
 * Uma sala da planta, em SVG: piso, paredes, cabeçalho (nome, etapa, número e
 * alertas), áreas (subsetores, postos, fila), bancadas e as pessoas nelas. Os
 * ITENS que andam (ambientes e peças desmembradas) NÃO são desenhados aqui:
 * ficam numa camada única (ItensSvg) para poderem ir de uma sala para outra.
 *
 * A sala é a FORMA que o gestor desenhou — retângulo, elipse ou polígono a mão
 * livre. Piso, textura e parede são o MESMO `<path>` (`paraPath` de
 * `dados/forma.js`), então o contorno torto é respeitado sem nenhum caso
 * especial. O conteúdo vem posicionado na maior caixa inscrita
 * (`planta.js` + `inscrita.js`), por isso nada aqui precisa saber o tipo da
 * forma.
 *
 * Nível de detalhe: `ampliacao` diz quanto o texto de dentro cresce para
 * ficar do mesmo tamanho na tela (1 = mapa de perto). Com `ampliacao=0`
 * (mapa muito afastado) ficam o nome, o número e os alertas da sala, as
 * bancadas e as pessoas como fichas com a cor da situação.
 *
 * Nada aqui é botão aninhado em botão: o alvo da sala (piso + cabeçalho) e
 * cada pessoa são irmãos dentro do grupo da sala.
 */
import React, { memo } from "react";
import { motion } from "framer-motion";
import {
  FiAlertTriangle,
  FiEdit2,
  FiPauseCircle,
  FiPlayCircle,
  FiScissors,
  FiSettings,
  FiUserX,
} from "react-icons/fi";

import { plural } from "../../utils/dinheiro";
import { paraPath } from "./dados/forma";
import { BANCADA, larguraTexto, truncarTexto } from "./planta";
import { ROTULO_SITUACAO_PESSOA, iniciais, primeiroNome } from "./pessoas";

const MARGEM_CABECALHO = 18;
const PILULA = { altura: 22, fonte: 12.5, pad: 9, icone: 13, espaco: 5, gap: 6 };
// Vão entre o texto da esquerda (nome, etapa) e o número da direita.
const VAO_DO_CABECALHO = 10;

// ------------------------------------------------------------ rótulos

/** "Corte: 2 ambientes, 1 atrasado" — o que o leitor de tela anuncia. */
export function rotuloDaSala(nome, resumo) {
  const quantos = resumo.filtrados != null
    ? `${resumo.filtrados} de ${plural(resumo.total, "ambiente", "ambientes")} no filtro`
    : plural(resumo.total, "ambiente", "ambientes");
  const partes = [];
  if (resumo.atrasadas) partes.push(plural(resumo.atrasadas, "atrasado", "atrasados"));
  if (resumo.pausados) partes.push(plural(resumo.pausados, "pausado", "pausados"));
  if (resumo.emProducao) partes.push(`${resumo.emProducao} em produção`);
  if (resumo.pecas) partes.push(plural(resumo.pecas, "peça desmembrada", "peças desmembradas"));
  return `${nome}: ${quantos}, ${partes.length ? partes.join(", ") : "sem atrasos"}`;
}

/** Pílulas de alerta do cabeçalho, na ordem de urgência. */
export function alertasDaSala(resumo, gargalo) {
  const alertas = [];
  if (resumo.atrasadas) {
    const texto = plural(resumo.atrasadas, "atrasado", "atrasados");
    alertas.push({ id: "atrasadas", tom: "danger", Icone: FiAlertTriangle, texto: gargalo ? `${texto} · gargalo` : texto });
  }
  if (resumo.pausados) {
    alertas.push({ id: "pausados", tom: "warning", Icone: FiPauseCircle, texto: plural(resumo.pausados, "pausado", "pausados") });
  }
  if (resumo.emProducao) {
    alertas.push({ id: "em_producao", tom: "info", Icone: FiPlayCircle, texto: `${resumo.emProducao} em produção` });
  }
  if (resumo.pecas) {
    alertas.push({ id: "pecas", tom: "neutral", Icone: FiScissors, texto: plural(resumo.pecas, "peça", "peças") });
  }
  if (resumo.semResponsavel) {
    alertas.push({ id: "sem_responsavel", tom: "neutral", Icone: FiUserX, texto: `${resumo.semResponsavel} sem responsável` });
  }
  return alertas;
}

/**
 * Linha de vínculo do cabeçalho: a ETAPA da planilha que alimenta a sala e, se
 * for o caso, que é ESTA que recebe os ambientes daquela etapa (várias salas
 * podem apontar para a mesma etapa; só uma recebe).
 */
export function textoDoVinculo(sala) {
  if (!sala.etapaRotulo) return "Sem etapa da planilha";
  return sala.recebeDaPlanilha ? `${sala.etapaRotulo} · recebe` : sala.etapaRotulo;
}

// --------------------------------------------------------- cabeçalho

function Pilulas({ alertas, x, y, larguraMax, hk }) {
  let cursor = x;
  const limite = x + larguraMax;
  const fonte = PILULA.fonte * hk;
  const desenhadas = [];
  for (const alerta of alertas) {
    const largura = (2 * PILULA.pad + PILULA.icone + PILULA.espaco) * hk + larguraTexto(alerta.texto, fonte);
    if (cursor + largura > limite) break;
    desenhadas.push({ ...alerta, x: cursor, largura });
    cursor += largura + PILULA.gap * hk;
  }
  const altura = PILULA.altura * hk;
  return desenhadas.map(({ id, tom, Icone, texto, x: px, largura }) => (
    <g key={id} className="pilula" data-tom={tom}>
      <rect x={px} y={y} width={largura} height={altura} rx={altura / 2} />
      <Icone x={px + PILULA.pad * hk} y={y + (altura - PILULA.icone * hk) / 2} size={PILULA.icone * hk} aria-hidden="true" />
      <text x={px + (PILULA.pad + PILULA.icone + PILULA.espaco) * hk} y={y + altura / 2} dy="0.35em" fontSize={fonte}>
        {texto}
      </text>
    </g>
  ));
}

/**
 * Texto do cabeçalho que cabe à esquerda do número, com o nome inteiro no
 * `title` quando foi cortado: numa sala estreita (elipse, corredor) o nome é a
 * primeira coisa a não caber, e "Expediç…" sem tooltip não identifica sala
 * nenhuma.
 */
function TextoDoCabecalho({ classe, texto, x, y, fonte, largura }) {
  const cabe = truncarTexto(texto, largura, fonte);
  if (!cabe) return null;
  return (
    <text className={classe} x={x} y={y} fontSize={fonte}>
      {cabe}
      {cabe !== texto ? <title>{texto}</title> : null}
    </text>
  );
}

function Cabecalho({ sala, resumo, gargalo }) {
  const { hk, nome } = sala;
  const { x, y, largura } = sala.cabecalho;
  const m = MARGEM_CABECALHO * hk;
  const numero = resumo.filtrados != null ? resumo.filtrados : resumo.total;
  const legenda = resumo.filtrados != null
    ? `de ${plural(resumo.total, "ambiente", "ambientes")}`
    : resumo.total === 1 ? "ambiente" : "ambientes";
  const alertas = alertasDaSala(resumo, gargalo);
  const fonteNome = 24 * hk;
  const fonteEtapa = 11.5 * hk;
  const fonteNumero = 34 * hk;
  const fonteLegenda = 12 * hk;
  // O nome divide a linha com o NÚMERO, e a etapa com a LEGENDA: cada um leva
  // o que o vizinho não usa. Uma fração fixa dava ao número de um dígito o
  // mesmo espaço de um de três e cortava o nome à toa.
  const vao = VAO_DO_CABECALHO * hk;
  const util = largura - 2 * m - vao;
  const legendaCompleta = legenda + (sala.postosOcultos ? " · postos ocultos" : "");
  const larguraDoNome = util - larguraTexto(String(numero), fonteNumero);
  const larguraDaEtapa = util - larguraTexto(legendaCompleta, fonteLegenda);
  return (
    <g className="cabecalho">
      <TextoDoCabecalho classe="sala-nome" texto={nome} x={x + m} y={y + 34 * hk} fonte={fonteNome} largura={larguraDoNome} />
      <TextoDoCabecalho
        classe="sala-etapa"
        texto={textoDoVinculo(sala)}
        x={x + m}
        y={y + 52 * hk}
        fonte={fonteEtapa}
        largura={larguraDaEtapa}
      />
      <text className="sala-numero" x={x + largura - m} y={y + 40 * hk} fontSize={fonteNumero} textAnchor="end">
        {numero}
      </text>
      <text className="sala-legenda" x={x + largura - m} y={y + 58 * hk} fontSize={fonteLegenda} textAnchor="end">
        {legendaCompleta}
        {sala.postosOcultos ? (
          <title>A sala está baixa para desenhar os postos. Aumente-a no Organizar.</title>
        ) : null}
      </text>
      {alertas.length ? (
        <Pilulas alertas={alertas} x={x + m} y={y + 64 * hk} larguraMax={largura - 2 * m} hk={hk} />
      ) : (
        <text className="sala-calma" x={x + m} y={y + 80 * hk} fontSize={12.5 * hk}>Sem atrasos</text>
      )}
    </g>
  );
}

// ------------------------------------------------------------ pessoas

function Pulso({ seq, cx, cy, r }) {
  if (!seq) return null;
  return (
    <motion.circle
      key={seq}
      className="pulso"
      cx={cx}
      cy={cy}
      r={r}
      initial={{ opacity: 0.7, scale: 1 }}
      animate={{ opacity: 0, scale: 1.9 }}
      transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
      aria-hidden="true"
    />
  );
}

// Com o texto ampliado, duas linhas (nome + o que faz) deixam de caber na
// vaga da pessoa: daí em diante fica só o nome, centrado no avatar.
const AMPLIACAO_MAX_DUAS_LINHAS = 1.25;
// Acima disso as iniciais não cabem mais dentro do avatar.
const AMPLIACAO_MAX_INICIAIS = 1.6;
// Mapa bem afastado: ficam só os nomes das pessoas e dos itens; os rótulos
// da bancada e da área sairiam por cima deles.
const AMPLIACAO_MAX_ROTULOS = 1.7;
// Letras do nome que precisam sobrar para valer a pena mostrá-lo cortado.
const MINIMO_DO_NOME = 3;

/**
 * Nome da pessoa que cabe ao lado do avatar, ou "" quando o que sobraria
 * não diz nada ("B…"). A mesma regra do rótulo do ambiente: nome pela metade
 * numa bancada é sujeira, e a ficha colorida já diz o que a pessoa faz.
 */
export function nomeQueCabe(nome, largura, fonte) {
  const texto = truncarTexto(nome, largura, fonte);
  if (!texto.endsWith("…")) return texto;
  return texto.length - 1 >= MINIMO_DO_NOME ? texto : "";
}

const Pessoa = memo(function Pessoa({ vaga, situacao, bancadaId, ampliacao, apagada, pulso, interativa }) {
  const { cx, cy, r, x, largura } = vaga;
  const nome = situacao?.colaborador?.nome || "Colaborador";
  const estado = situacao?.situacao || "livre";
  const atividade = situacao?.atividade?.texto || "";
  const k = r / BANCADA.AVATAR_R;
  const a = ampliacao || 0;
  const duasLinhas = a > 0 && a <= AMPLIACAO_MAX_DUAS_LINHAS && Boolean(atividade);
  const xTexto = cx + r + 7 * k;
  const larguraDisponivel = x + largura - xTexto;
  const fonteNome = 12.5 * k * a;
  const fonteAtividade = 11 * k * a;
  // Longe demais para as iniciais: a pessoa vira uma ficha cheia da cor do
  // que está fazendo — de relance é o que se lê na sala.
  const longe = !(a > 0) || a > AMPLIACAO_MAX_INICIAIS;
  const nomeVisivel = a > 0 ? nomeQueCabe(primeiroNome(nome), larguraDisponivel, fonteNome) : "";
  return (
    <g
      className="pessoa"
      data-pessoa={`${bancadaId}|${vaga.colaboradorId}`}
      data-situacao={estado}
      data-longe={longe ? "sim" : undefined}
      data-apagado={apagada ? "sim" : undefined}
      role="button"
      tabIndex={interativa ? 0 : -1}
      aria-label={`${nome}: ${ROTULO_SITUACAO_PESSOA[estado] || "Livre"}${atividade && estado !== "livre" ? `, ${atividade}` : ""}`}
    >
      <Pulso seq={pulso} cx={cx} cy={cy} r={r} />
      {/* Sombra curta: a pessoa fica EM PÉ na bancada em vez de impressa
          nela. É o mesmo truque do item e da sala, na escala da ficha. */}
      <circle className="pessoa-sombra" cx={cx} cy={cy + 1.6 * k} r={r} aria-hidden="true" />
      <circle className="pessoa-anel" cx={cx} cy={cy} r={r} />
      {estado !== "livre" && !longe && (
        <circle className="pessoa-selo" cx={cx + r * 0.72} cy={cy + r * 0.72} r={4.5 * k} />
      )}
      {/* De longe as iniciais viram ruído (~5 px): fica o anel, que já diz a situação. */}
      {a > 0 && a <= AMPLIACAO_MAX_INICIAIS && (
        <text className="pessoa-iniciais" x={cx} y={cy} dy="0.35em" fontSize={10.5 * k * a} textAnchor="middle">
          {iniciais(nome)}
        </text>
      )}
      {nomeVisivel && (
        <text
          className="pessoa-nome"
          x={xTexto}
          y={duasLinhas ? cy - 2 * k * a : cy}
          dy={duasLinhas ? undefined : "0.35em"}
          fontSize={fonteNome}
        >
          {nomeVisivel}
        </text>
      )}
      {duasLinhas && (
        <text className="pessoa-atividade" x={xTexto} y={cy + 12 * k * a} fontSize={fonteAtividade}>
          {truncarTexto(atividade, larguraDisponivel, fonteAtividade)}
        </text>
      )}
    </g>
  );
});

// ---------------------------------------------------------- bancadas

function textoDaBancada(bancada, tarefas) {
  const extras = [];
  if (bancada.pessoasOcultas > 0) extras.push(`+${bancada.pessoasOcultas}`);
  if (tarefas) extras.push(plural(tarefas, "tarefa", "tarefas"));
  return extras.length ? `${bancada.nome} · ${extras.join(" · ")}` : bancada.nome;
}

function Bancada({ bancada, k, ampliacao, apagada, tarefas, situacoes, pessoaApagada, pulsos, interativa }) {
  const { x, y, largura, altura } = bancada;
  const a = ampliacao || 0;
  const fonteRotulo = 12 * k * a;
  return (
    <g className="bancada" data-bancada={bancada.id} data-virtual={bancada.virtual ? "sim" : undefined} data-apagado={apagada ? "sim" : undefined}>
      <rect className="bancada-tampo" x={x} y={y} width={largura} height={altura} rx={8 * k} />
      {/* Aresta de baixo = a espessura do tampo. É o que separa o móvel do
          chão da área quando os dois são claros (ou os dois escuros). */}
      <line className="bancada-aresta" x1={x + 9 * k} y1={y + altura - 2} x2={x + largura - 9 * k} y2={y + altura - 2} />
      {a > 0 && a <= AMPLIACAO_MAX_ROTULOS && (
        // A linha de base desce junto com a fonte, para o rótulo não sair
        // pelo topo da bancada quando o texto está ampliado.
        <text className="bancada-rotulo" x={x + BANCADA.PAD * k} y={y + (8 + 11 * a) * k} fontSize={fonteRotulo}>
          {truncarTexto(textoDaBancada(bancada, tarefas), largura - 2 * BANCADA.PAD * k, fonteRotulo)}
        </text>
      )}
      {bancada.vagas.map((vaga, i) => (
        // Espaço livre da bancada: contorno tracejado, o item "senta" em cima.
        <rect key={i} className="vaga" x={vaga.x} y={vaga.y} width={vaga.largura} height={vaga.altura} rx={5 * k} />
      ))}
      {bancada.pessoas.map((vaga) => {
        const situacao = situacoes.get(vaga.colaboradorId);
        return (
          <Pessoa
            key={vaga.colaboradorId}
            vaga={vaga}
            situacao={situacao}
            bancadaId={bancada.id}
            ampliacao={ampliacao}
            apagada={pessoaApagada(situacao)}
            pulso={pulsos?.get(`pes:${vaga.colaboradorId}`)}
            interativa={interativa}
          />
        );
      })}
    </g>
  );
}

// -------------------------------------------------------------- áreas

function rotuloDaArea(area) {
  const partes = [area.rotulo];
  if (area.totalItens) partes.push(String(area.totalItens));
  if (area.bancadasOcultas) partes.push(`+${plural(area.bancadasOcultas, "posto", "postos")}`);
  return partes.join(" · ");
}

function Area({ area, k, ampliacao, destaque, tarefasPorPosto, situacoes, pulsos, interativa }) {
  const { x, y, largura, altura } = area;
  const a = ampliacao || 0;
  const fonteRotulo = 12.5 * k * a;
  return (
    <g className="area" data-tipo={area.tipo} data-apagado={destaque.areaApagada(area) ? "sim" : undefined}>
      {/* Postos soltos desenhados pelo gestor ficam sem moldura: um retângulo
          "Postos" em volta deles mentiria sobre o desenho. */}
      {!area.semMoldura && <rect className="area-piso" x={x} y={y} width={largura} height={altura} rx={8 * k} />}
      {!area.semMoldura && a > 0 && a <= AMPLIACAO_MAX_ROTULOS && (
        <text className="area-rotulo" x={x + 12 * k} y={y + (16 + 4 * a) * k} fontSize={fonteRotulo}>
          {truncarTexto(rotuloDaArea(area), largura - 24 * k, fonteRotulo)}
        </text>
      )}
      {area.bancadas.map((bancada) => (
        <Bancada
          key={bancada.id}
          bancada={bancada}
          // Bancada desenhada à mão tem a escala do tamanho que foi desenhada.
          k={bancada.k ?? k}
          ampliacao={ampliacao}
          apagada={destaque.bancadaApagada(bancada)}
          tarefas={tarefasPorPosto.get(bancada.id) || 0}
          situacoes={situacoes}
          pessoaApagada={destaque.pessoaApagada}
          pulsos={pulsos}
          interativa={interativa}
        />
      ))}
    </g>
  );
}

// --------------------------------------------------------------- sala

/** Convite tracejado no canto do interior da sala ("Configurar setor"). */
function Convite({ sala }) {
  const { interior, hk } = sala;
  const fonte = 12.5 * hk;
  const texto = "Configurar setor";
  const larguraConvite = (16 + 14 + 6) * hk + larguraTexto(texto, fonte);
  const cx = interior.x + interior.largura - MARGEM_CABECALHO * hk - larguraConvite;
  const cy = interior.y + interior.altura - (MARGEM_CABECALHO + 26) * hk;
  return (
    <g className="convite" data-configurar={sala.setorId} role="button" tabIndex={0} aria-label={`Configurar o setor ${sala.nome}`}>
      <rect x={cx} y={cy} width={larguraConvite} height={26 * hk} rx={13 * hk} />
      <FiSettings x={cx + 8 * hk} y={cy + 6 * hk} size={14 * hk} aria-hidden="true" />
      <text x={cx + (8 + 14 + 6) * hk} y={cy + 13 * hk} dy="0.35em" fontSize={fonte}>{texto}</text>
    </g>
  );
}

/**
 * Onde fica cada alça de puxar, em torno da caixa envolvente da sala. As
 * paredes viram FAIXAS (dá para pegar em qualquer ponto do lado, não só num
 * quadradinho) e os cantos, quadrados. `faixa` e `grip` já vêm no mundo.
 */
export function alcasDePuxar({ x, y, largura, altura }, faixa) {
  const meia = faixa / 2;
  const d = x + largura;
  const b = y + altura;
  return [
    // Cantos primeiro no DOM, faixas depois: onde se cruzam, o canto é que
    // fica por cima (é o gesto mais específico — puxa as duas medidas).
    { lado: "n", x: x + meia, y: y - meia, largura: Math.max(0, largura - faixa), altura: faixa },
    { lado: "s", x: x + meia, y: b - meia, largura: Math.max(0, largura - faixa), altura: faixa },
    { lado: "w", x: x - meia, y: y + meia, largura: faixa, altura: Math.max(0, altura - faixa) },
    { lado: "e", x: d - meia, y: y + meia, largura: faixa, altura: Math.max(0, altura - faixa) },
    { lado: "nw", x: x - meia, y: y - meia, largura: faixa, altura: faixa },
    { lado: "ne", x: d - meia, y: y - meia, largura: faixa, altura: faixa },
    { lado: "sw", x: x - meia, y: b - meia, largura: faixa, altura: faixa },
    { lado: "se", x: d - meia, y: b - meia, largura: faixa, altura: faixa },
  ];
}

/**
 * Alças do Organizar: puxar por QUALQUER parede ou canto redimensiona (pedido
 * do dono) e o lápis abre o redesenho a mão livre daquela sala.
 *
 * Moram numa camada ACIMA de todas as salas (MapaSvg), e não dentro de cada
 * uma: a alça fica na caixa envolvente, e numa sala em "L" o canto da caixa
 * cai no recorte — onde a sala vizinha, desenhada depois, a cobria. Foi assim
 * que o setor Industrialização ficou sem alça nenhuma.
 *
 * Em PIXELS DE TELA (divididas pela escala), para o alvo de toque não
 * encolher com o mapa afastado.
 */
export function AlcasDaSala({ sala, escala, selecionada }) {
  const { setorId, x, y, largura, altura } = sala;
  // 32 px de faixa invisível: pega-se a parede sem mira; o visível é fino.
  const faixa = Math.min(32 / escala, largura / 3, altura / 3);
  const grip = Math.min(12 / escala, faixa);
  const barra = { comprido: Math.min(36 / escala, largura / 3, altura / 3), fino: 6 / escala };
  // 44 px de tela: o mesmo alvo mínimo de toque do resto da casa.
  const lapis = Math.min(44 / escala, largura / 2, altura / 2);
  const folga = 6 / escala;
  const alcas = alcasDePuxar(sala, faixa);
  const cx = x + largura / 2;
  const cy = y + altura / 2;
  const visiveis = [
    { lado: "nw", x, y }, { lado: "ne", x: x + largura, y },
    { lado: "sw", x, y: y + altura }, { lado: "se", x: x + largura, y: y + altura },
  ];
  const barras = [
    { lado: "n", x: cx - barra.comprido / 2, y: y - barra.fino / 2, largura: barra.comprido, altura: barra.fino },
    { lado: "s", x: cx - barra.comprido / 2, y: y + altura - barra.fino / 2, largura: barra.comprido, altura: barra.fino },
    { lado: "w", x: x - barra.fino / 2, y: cy - barra.comprido / 2, largura: barra.fino, altura: barra.comprido },
    { lado: "e", x: x + largura - barra.fino / 2, y: cy - barra.comprido / 2, largura: barra.fino, altura: barra.comprido },
  ];
  return (
    <g className="alcas" data-selecionada={selecionada ? "sim" : undefined}>
      {barras.map((b) => (
        <rect key={`barra-${b.lado}`} className="alca" data-lado={b.lado} data-alca={setorId} x={b.x} y={b.y} width={b.largura} height={b.altura} rx={barra.fino / 2} />
      ))}
      {visiveis.map((v) => (
        <rect
          key={`canto-${v.lado}`}
          className="alca"
          data-lado={v.lado}
          data-alca={setorId}
          x={v.x - grip / 2}
          y={v.y - grip / 2}
          width={grip}
          height={grip}
          rx={grip / 4}
        />
      ))}
      {alcas.map((a) => (
        <rect
          key={`alvo-${a.lado}`}
          className="alca-alvo"
          data-lado={a.lado}
          data-alca={setorId}
          x={a.x}
          y={a.y}
          width={a.largura}
          height={a.altura}
        >
          <title>{`Puxe para mudar o tamanho de ${sala.nome}`}</title>
        </rect>
      ))}
      {/* Por último, por cima das faixas: o lápis encosta na parede de baixo
          e na da esquerda, e a faixa invisível roubaria o toque nele. Canto de
          BAIXO À ESQUERDA: cima à direita é do número, cima à esquerda, do nome. */}
      <g className="redesenhar" data-redesenhar={setorId} role="button" tabIndex={0} aria-label={`Redesenhar o setor ${sala.nome}`}>
        <rect x={x + folga} y={y + altura - lapis - folga} width={lapis} height={lapis} rx={6 / escala} />
        <FiEdit2 x={x + folga + lapis * 0.25} y={y + altura - lapis - folga + lapis * 0.25} size={lapis * 0.5} aria-hidden="true" />
      </g>
    </g>
  );
}

/**
 * Sala inteira. `resumo` = resumoDaSala; `destaque` = montarDestaque;
 * `situacoes` = Map de situacaoDasPessoas; `tarefasPorPosto` = Map<postoId, n>.
 */
function SalaSvg({
  sala, resumo, gargalo, apagada, ampliacao, organizando, selecionada, podeConfigurar,
  destaque, situacoes, tarefasPorPosto, pulsos,
}) {
  const { setorId, k } = sala;
  const interativa = !organizando;
  const contorno = paraPath(sala.forma);
  return (
    <g
      className="sala"
      data-sala={setorId}
      // Parede vermelha só no gargalo: se toda sala com atraso ficasse
      // vermelha, nenhuma se destacaria. O atraso das outras está na pílula.
      data-tom={gargalo ? "danger" : "neutral"}
      data-apagado={apagada ? "sim" : undefined}
      data-organizando={organizando ? "sim" : undefined}
      data-selecionada={selecionada ? "sim" : undefined}
      role="group"
      aria-label={`Sala ${sala.nome}`}
    >
      <g className="sala-alvo" data-setor={setorId} role="button" tabIndex={0} aria-label={rotuloDaSala(sala.nome, resumo)}>
        <path className="piso" d={contorno} />
        <path className="piso-textura" d={contorno} />
        {/* Sala toda ocupada pelo desenho do gestor: o cabeçalho é uma placa
            do lado de fora, com fundo próprio (ela fica sobre o chão do mapa). */}
        {sala.cabecalhoFora && (
          <rect
            className="placa"
            x={sala.cabecalho.x}
            y={sala.cabecalho.y}
            width={sala.cabecalho.largura}
            height={sala.cabecalho.altura}
            rx={8 * sala.hk}
          />
        )}
        <Cabecalho sala={sala} resumo={resumo} gargalo={gargalo} />
      </g>
      {sala.areas.map((area) => (
        <Area
          key={area.id}
          area={area}
          k={k}
          ampliacao={ampliacao}
          destaque={destaque}
          tarefasPorPosto={tarefasPorPosto}
          situacoes={situacoes}
          pulsos={pulsos}
          interativa={interativa}
        />
      ))}
      {sala.semEstrutura && podeConfigurar && !organizando && <Convite sala={sala} />}
      <path className="parede" d={contorno} />
    </g>
  );
}

export default memo(SalaSvg);
